import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { coverDocStoreWarning } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** What the editor says about the first version that it made (design cover-editor e1 to e4). */
export type FirstVersionNote = 'aiMaking' | 'aiReady' | 'bank' | 'aiUnavailable' | null;

const FIRST_MESSAGES = {
  aiMaking: coverEditorMessages.firstAiMaking,
  aiReady: coverEditorMessages.firstAiReady,
  bank: coverEditorMessages.firstBank,
  aiUnavailable: coverEditorMessages.firstAiUnavailable,
} as const;

interface EditorNoticesProps {
  firstVersion: FirstVersionNote;
  onDismissFirst: () => void;
}

/**
 * The short notes over the stage: the example artist is read only; what the first version is made
 * of; and the store note when a text box holds text that is not the title or the artist (owner
 * decision D9).
 */
const EditorNoticesComponent: React.FC<EditorNoticesProps> = ({ firstVersion, onDismissFirst }) => {
  const intl = useIntl();
  const { state, data, readOnly } = useCoverEditor();
  const hasStoreWarning = useMemo(
    () => coverDocStoreWarning(state.doc, { title: data.song.title, artist: data.song.artistName }),
    [data.song.artistName, data.song.title, state.doc]
  );
  if (!readOnly && firstVersion === null && !hasStoreWarning) {
    return null;
  }
  return (
    <div className={styles.notices}>
      {readOnly && <p className={styles.notice}>{intl.formatMessage(coverEditorMessages.readOnly)}</p>}
      {firstVersion !== null && !readOnly && (
        <p className={styles.notice} role="status">
          <span className={styles.noticeDot} />
          {intl.formatMessage(FIRST_MESSAGES[firstVersion])}
          <button type="button" className={styles.linkButton} onClick={onDismissFirst}>
            {intl.formatMessage(coverEditorMessages.noticeDismiss)}
          </button>
        </p>
      )}
      {hasStoreWarning && (
        <p className={`${styles.notice} ${styles.noticeWarn}`} role="note">
          {intl.formatMessage(coverEditorMessages.storeNote)}
        </p>
      )}
    </div>
  );
};

export const EditorNotices = TypedMemo(EditorNoticesComponent);
